import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// Configuración base del API
const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

console.log('API_BASE_URL configured as:', API_BASE_URL);
console.log('Environment variables:', {
  VITE_API_URL: import.meta.env.VITE_API_URL,
  MODE: import.meta.env.MODE,
  DEV: import.meta.env.DEV,
});

// Crear instancia de axios
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    Accept: 'application/json; charset=utf-8',
  },
});

// Interceptor para requests - agregar token de autenticación
apiClient.interceptors.request.use(
  (config) => {
    console.log(
      'Making API request to:',
      `${config.baseURL || ''}${config.url || ''}`
    );
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Interceptor para responses - manejo de errores globales
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    // 401 en /auth/login = credenciales incorrectas, NO sesión expirada.
    // (En el endpoint de login todavía no existe sesión que pueda expirar.)
    const url = (error.config?.url || '') as string;
    const isLoginRequest = url.includes('/auth/login');
    if (error.response?.status === 401 && isLoginRequest) {
      const customError = new Error(
        'Nombre de usuario y/o contraseña incorrectos. Por favor revise lo que está ingresando.'
      ) as any;
      customError.response = error.response;
      customError.status = 401;
      return Promise.reject(customError);
    }

    // 401 en cualquier otro endpoint = token expirado o inválido
    if (error.response?.status === 401) {
      console.log(
        '🚨 [API Interceptor] Token rechazado (401), limpiando sesión y redirigiendo...',
        {
          url: error.config?.url,
          method: error.config?.method,
        }
      );
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(
        new Error('Sesión expirada. Por favor, inicia sesión nuevamente.')
      );
    }

    // Error de red
    if (!error.response) {
      return Promise.reject(
        new Error('Error de conexión. Verifica tu conexión a internet.')
      );
    }

    // Otros errores del servidor
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      'Error del servidor';

    // Crear un error personalizado que preserve la información del response
    const customError = new Error(message) as any;
    customError.response = error.response;
    customError.status = error.response?.status;

    return Promise.reject(customError);
  }
);

// Tipos para las respuestas de la API
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  token: string;
  user: {
    id: number;
    nombre: string;
    email: string;
    rol: string;
  };
  expiresIn: string;
}

// Métodos HTTP básicos
export const api = {
  // GET request
  get: <T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> => {
    return apiClient.get(url, config);
  },

  // POST request
  post: <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> => {
    return apiClient.post(url, data, config);
  },

  // PUT request
  put: <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> => {
    return apiClient.put(url, data, config);
  },

  // DELETE request
  delete: <T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> => {
    return apiClient.delete(url, config);
  },

  // PATCH request
  patch: <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> => {
    return apiClient.patch(url, data, config);
  },
};

export default apiClient;
